/**
 * Time Helper Functions for CarBooking Module
 * Funciones auxiliares para manejo de horas y turnos
 */

import { normalizeDateOnly } from './dateHelpers';

/**
 * Convierte una hora en formato HH:mm a minutos totales
 * @param {string} hm - Hora en formato HH:mm
 * @returns {number} Minutos totales desde medianoche
 */
export function toMinutes(hm) {
    const [h, m] = (hm || "0:0").split(":").map(Number);
    return h * 60 + m;
}

/**
 * Verifica si un rango de horas está dentro de alguno de los intervalos permitidos
 * @param {string} startHM - Hora inicio en formato HH:mm
 * @param {string} endHM - Hora fin en formato HH:mm
 * @param {Array} intervals - Array de intervalos {start, end}
 * @returns {boolean} true si está dentro de algún intervalo
 */
export function isInsideInterval(startHM, endHM, intervals) {
    let s = toMinutes(startHM);
    let e = toMinutes(endHM);

    // Si el rango solicitado cruza la medianoche (ej: 22:00 a 05:00)
    // e será menor que s. Ajustamos e sumando 24h (1440 min).
    if (e <= s) {
        e += 1440;
    }

    return (intervals || []).some((iv) => {
        let ivS = toMinutes(iv.start);
        let ivE = toMinutes(iv.end);

        // Si el intervalo del turno cruza la medianoche (ej: 20:00 a 06:00)
        if (ivE <= ivS) {
            ivE += 1440;
        }

        // Verificamos contención:
        // El rango solicitado [s, e] debe estar totalmente dentro de [ivS, ivE]
        return s >= ivS && e <= ivE;
    });
}

/**
 * Construye los intervalos de turno para un chofer en una fecha específica,
 * considerando excepciones de turno
 * @param {Date} date - Fecha a consultar
 * @param {number} driverId - ID del chofer
 * @param {Array} baseIntervals - Intervalos base del turno [{start, end}]
 * @param {Array} excepciones - Array de excepciones de turno
 * @returns {Array} Intervalos finales para ese día
 */
export function buildDailyIntervalsForDriver(
    date,
    driverId,
    baseIntervals,
    excepciones
) {
    if (!driverId || !date) return baseIntervals || [];

    const target = normalizeDateOnly(date);
    if (!target) return baseIntervals || [];

    const relevant = (excepciones || []).filter((ex) => {
        const exChofer =
            ex.idChofer ??
            ex.IdChofer ??
            ex["idChofer"] ??
            ex["IdChofer"] ??
            null;
        if (Number(exChofer) !== Number(driverId)) return false;
        if (ex.activo === false || ex.Activo === false) return false;

        const fi = normalizeDateOnly(ex.fechaInicio ?? ex.FechaInicio);
        const ff = normalizeDateOnly(
            ex.fechaFin ?? ex.FechaFin ?? ex.fechaInicio ?? ex.FechaInicio
        );
        if (!fi) return false;

        const end = ff || fi;
        const t = target.getTime();
        return t >= fi.getTime() && t <= end.getTime();
    });

    // Sin excepciones → horario base
    if (relevant.length === 0) return baseIntervals || [];

    // Hay una o varias excepciones → el día se rige SOLO por estas
    const exIntervals = relevant
        .map((ex) => {
            const hIni = String((ex.horaInicio ?? ex.HoraInicio) || "").slice(0, 5);
            const hFin = String((ex.horaFin ?? ex.HoraFin) || "").slice(0, 5);
            if (!hIni || !hFin) return null;
            // Permitimos excepciones que crucen la medianoche (hFin <= hIni)
            // if (toMinutes(hFin) <= toMinutes(hIni)) return null; 
            return { start: hIni, end: hFin };
        })
        .filter(Boolean);

    // Si por alguna razón no quedó ningún intervalo válido, devolvemos base
    return exIntervals.length > 0 ? exIntervals : baseIntervals || [];
}
